(ns minorodata.ai-chat.events
  (:require [applied-science.js-interop :as js-interop]
            [clojure.string :as string]
            [minorodata.app.handlers :refer [reg-event-fx dispatch reg-event-db]]
            [minorodata.app.utils :as app.utils]
            [minorodata.sql-console.model :refer [->sql-metadata ->sql-editor]]
            [minorodata.ai-chat.byol :as byol]
            [minorodata.ai-chat.confirmation :as confirmation]
            [minorodata.ai-chat.model :refer [->ai-settings]]
            [minorodata.sandbox.api :refer [parse-service-transform]]
            [minorodata.sandbox.model :refer [->sandbox-transform]]
            [mold-http.core :refer [reg-event-http]]
            [mold-forms.core]
            [oxbow.re-frame :as o]))


(defn now []
  (/ (js/Date.now) 1000))

(def ^:private transform-proposal-re
  #"<TRANSFORM_PROPOSAL>([\s\S]*?)</TRANSFORM_PROPOSAL>")

(defn- extract-tag [body tag]
  (when-let [[_ v] (re-find (re-pattern (str "<" tag ">\\s*([\\s\\S]*?)\\s*</" tag ">")) body)]
    v))

(defn parse-transform-proposal
  [text]
  (when (string? text)
    (when-let [[_ body] (re-find transform-proposal-re text)]
      (let [action (extract-tag body "ACTION")
            sql    (extract-tag body "SQL")]
        (when (and action sql)
          {:action    action
           :name      (extract-tag body "NAME")
           :group-id  (extract-tag body "GROUP_ID")
           :master-id (extract-tag body "MASTER_ID")
           :old-sql   (or (extract-tag body "OLD_SQL") "")
           :sql       sql})))))

(defn- split-proposal-message
  [message]
  (if-let [proposal (parse-transform-proposal (:text message))]
    (let [preamble (-> (:text message)
                       (string/replace transform-proposal-re "")
                       string/trim)
          proposal-msg (-> message
                           (dissoc :text)
                           (assoc :type "transform_proposal"
                                  :proposal proposal
                                  :timestamp (+ (:timestamp message 0) 0.0001)))]
      (if (seq preamble)
        [(assoc message :text preamble) proposal-msg]
        [proposal-msg]))
    [message]))

(def ^:private sandbox-tool-names
  #{"create_sandbox_transform_tool" "edit_sandbox_transform_tool"})

(defn- sandbox-success-response? [message]
  (and (= "function_response" (:type message))
       (contains? sandbox-tool-names (:name message))
       (let [resp (:response message)]
         (and (map? resp) (not (contains? resp :error))))))

(defn- sandbox-success-snack [message]
  (when (sandbox-success-response? message)
    [:app.events/add-snack :success
     (if (= "create_sandbox_transform_tool" (:name message))
       "Transform created in Sandbox"
       "Transform updated in Sandbox")]))

(defn- sandbox-store-events
  [db message]
  (when (sandbox-success-response? message)
    (let [transform (-> (:response message) parse-service-transform ->sandbox-transform)
          master-id (:sandbox-transform/master-id transform)
          group-id  (:sandbox-transform/group-id transform)
          transform-name   (:sandbox-transform/name transform)
          create?   (= "create_sandbox_transform_tool" (:name message))
          store     (if create?
                      [:sandbox-transform/add transform]
                      [:sandbox-transform/update [:sandbox-transform/master-id master-id] transform])
          base      [store
                     [:sandbox.api/evaluate-pipelines]
                     [:sandbox.events/watch-execution]
                     [:sandbox.api/fetch-sandbox-updates]]]
      (cond-> base
        (:pipeline-editor db)
        (conj [:pipeline-editor.events/switch-mode false]
              [:pipeline-editor.events/open-tab
               {:id master-id :group-id group-id :type "transform" :name transform-name}]
              [:app.events/navigate-query {:tab-preview-type     "transform"
                                           :tab-preview-id       master-id
                                           :tab-preview-group-id group-id}])))))

(defn- attach-success-to-last-proposal
  [messages response]
  (let [created  {:master-id (or (:master_id response) (:master-id response))
                  :group-id  (or (:group_id response)  (:group-id response))
                  :name      (:name response)
                  :sql       (:sql response)}
        idx (->> messages
                 (map-indexed vector)
                 (filter (fn [[_ m]] (= "transform_proposal" (:type m))))
                 last
                 first)]
    (if idx
      (update (vec messages) idx assoc-in [:proposal :created-transform] created)
      messages)))

(defn- mark-superseded [message]
  (if (or (get-in message [:proposal :created-transform])
          (= :superseded (get-in message [:proposal :status])))
    message
    (assoc-in message [:proposal :status] :superseded)))

(defn- supersede-pending-proposals
  [messages]
  (let [proposal-idxs (->> messages
                           (map-indexed vector)
                           (filter (fn [[_ message]] (= "transform_proposal" (:type message))))
                           (map first))
        older         (butlast proposal-idxs)]
    (reduce (fn [msgs idx] (update msgs idx mark-superseded))
            (vec messages)
            older)))

(reg-event-fx
  :ai-chat/on-event
  (fn [{:keys [db]} [{:keys [data] :as event}]]
    (let [message (js->clj (js/JSON.parse (:data event)) :keywordize-keys true)

          messages (sort-by :timestamp (:ai-chat-messages db))
          prior (if (= "confirmation_request" (:type message))
                  (confirmation/supersede-pending messages)
                  messages)
          last-message (last messages)
          new-messages (if (and (= "assistant" (:author message))
                                (= "text" (:type last-message))
                                (= "text" (:type message))
                                (= (:invocation_id last-message) (:invocation_id message)))
                         (if (:partial message)
                           ;; Append text if message is partial
                           (concat (butlast messages) [(update last-message :text str (:text message))])
                           ;; Replace all text is final
                           (concat (butlast messages) [message]))
                         ;; drop content-less payloads — they break the merge above & duplicate the answer (FT-2554)
                         (if (:type message)
                           (concat prior [message])
                           messages))
          expanded (if (and (= "assistant" (:author message))
                            (= "text" (:type message))
                            (not (:partial message)))
                     (concat (butlast new-messages)
                             (split-proposal-message (last new-messages)))
                     new-messages)
          new-proposal? (and (= "assistant" (:author message))
                             (= "text" (:type message))
                             (not (:partial message))
                             (some? (parse-transform-proposal (:text message))))
          superseded (if new-proposal?
                       (supersede-pending-proposals expanded)
                       expanded)
          stamped  (if (sandbox-success-response? message)
                     (attach-success-to-last-proposal superseded (:response message))
                     superseded)
          snack    (sandbox-success-snack message)
          store-events (sandbox-store-events db message)
          dispatches (cond-> []
                       snack        (conj snack)
                       store-events (into store-events))]
      (cond-> {:db (assoc db :ai-chat-messages stamped)}
        (seq dispatches) (assoc :dispatch-n dispatches)))))

(reg-event-db
  :ai-chat/add-user-message
  (fn [db [message]]
    (let [invocation-id (random-uuid)
          now (now)
          author "user"]
      (-> db
          (update :ai-chat-messages confirmation/supersede-pending)
          (update :ai-chat-messages conj {:invocation-id invocation-id
                                          :timestamp     now
                                          :author        author
                                          :type          :text
                                          :text          message})))))


(reg-event-db
  :ai-chat/clear-chat
  (fn [db _]
    (-> db
        (dissoc :ai-chat-messages)
        (dissoc :ai-chat-conversation-id)
        (dissoc :ai-chat-model))))


(reg-event-db
  :ai-chat/set-model
  (fn [db [model]]
    (assoc db :ai-chat-model model)))


(reg-event-db
  :ai-chat/set-conversation-id
  (fn [db [conversation-id]]
    (assoc db :ai-chat-conversation-id conversation-id)))


(reg-event-db
  :ai-chat/set-loading
  (fn [db [loading?]]
    (assoc db :ai-chat-loading loading?)))


(reg-event-db
  :ai-chat/mark-confirmation
  (fn [db [id answer]]
    (update db :ai-chat-messages confirmation/mark-answered id answer)))


(reg-event-db
  :ai-chat/confirmation-failed
  (fn [db [id]]
    (-> db
        (update :ai-chat-messages confirmation/mark-answered id :failed)
        (assoc :ai-chat-loading false))))


;; Only supporting single transforms for now
(reg-event-db
 :ai-chat/set-context
 (fn [db [transform]]
   (let [{:keys [master-id group-id name command]} (app.utils/drop-ns-in-keys transform)]
     (assoc db :ai-chat-context
            {:master-id master-id
             :group-id  group-id
             :name      name
             :sql       (or command "")}))))

(reg-event-db
 :ai-chat/clear-context
 (fn [db _]
   (dissoc db :ai-chat-context)))

;; A one-shot message to prefill the chat input with (e.g. "debug this failure").
;; `ai-modal` seeds its prompt textarea from this and immediately clears it, so the
;; user can edit or send it themselves — we never auto-send.
(reg-event-db
 :ai-chat/set-draft
 (fn [db [draft]]
   (assoc db :ai-chat-draft draft)))

(reg-event-db
 :ai-chat/clear-draft
 (fn [db _]
   (dissoc db :ai-chat-draft)))

(reg-event-db
 :ai-chat/dismiss-editor
 (fn [db _]
   (assoc db :ai-chat-editor-dismissed? true)))

(reg-event-db
 :ai-chat/reattach-editor
 (fn [db _]
   (dissoc db :ai-chat-editor-dismissed?)))

(reg-event-db
 :ai-chat/set-pending-prompt
 (fn [db [prompt]]
   (assoc db :ai-chat-pending-prompt prompt)))

(reg-event-db
 :ai-chat/clear-pending-prompt
 (fn [db _]
   (dissoc db :ai-chat-pending-prompt)))

(reg-event-fx
 :ai-chat/navigate-to-pe-with-tab
 (fn [{:keys [db]} [{:keys [master-id group-id name]}]]
   (let [tab-dispatches [[:pipeline-editor.events/switch-mode false]
                         [:pipeline-editor.events/open-tab
                          {:id master-id :group-id group-id :type "transform" :name name}]
                         [:app.events/navigate-query {:tab-preview-type     "transform"
                                                      :tab-preview-id       master-id
                                                      :tab-preview-group-id group-id}]]]
     (if (:pipeline-editor db)
       {:dispatch-n (into [[:sql-console-modal.events/toggle-modal false]] tab-dispatches)}
       {:dispatch [:sql-console-modal.events/toggle-modal false]
        :async-flow
        {:first-dispatch [:app.events/navigate :pipeline-editor]
         :rules [{:when     :seen?
                  :events   :pipeline-editor.events/init
                  :dispatch-n tab-dispatches
                  :halt?    true}]}}))))


(defn clear-server-chat-request [{:keys [db]} _]
  (let [conv-id (:ai-chat-conversation-id db)]
    {:http-xhrio
     {:id      :post-clear-server-chat
      :method  :post
      ; :url "http://127.0.0.1:8000/ai-chatbot/clear"
      :url     "/ai-chatbot/clear"
      :headers {"Content-Type" "application/json"}
      :body    (js/JSON.stringify
                 (clj->js (cond-> {}
                            conv-id (assoc :conversation_id conv-id))))}}))

(defn clear-server-chat-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to clear chat history"]}))

(reg-event-http
 :ai-chat/clear-server-chat
 clear-server-chat-request
 clear-server-chat-callback)


(defn get-ai-settings-request [_ _]
  {:http-xhrio
   {:id  :get-ai-settings
    :method :get
    :url "/api/ai-settings"}})

(defn get-ai-settings-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch Kai settings"]}
    {:dispatch-n [[:ai-settings/delete]
                  [:ai-settings/add  (->ai-settings results)]]}))

(reg-event-http
 :ai-chat/get-settings
 get-ai-settings-request
 get-ai-settings-callback)


(defn update-ai-settings-request [_ [params capture]]
  {:http-xhrio
   {:id        :update-ai-settings
    :method    :post
    :params    params
    :cb-params {:capture capture}
    :url       "/api/admin/ai-settings"}})

(defn update-ai-settings-callback [_ [error results {:keys [capture]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error (byol/settings-error-message error)]
                  [:ai-chat/get-settings]]}
    (cond-> {:dispatch [:ai-chat/get-settings]}
      capture (assoc :posthog/capture capture))))

(reg-event-http
 :ai-chat/update-settings
 update-ai-settings-request
 update-ai-settings-callback)


(defn fetch-provider-credentials-request [_ _]
  {:http-xhrio
   {:id     :fetch-provider-credentials
    :method :get
    :url    "/api/admin/ai-provider-credentials"}})

(defn fetch-provider-credentials-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch provider keys"]}
    {:dispatch [:ai-chat/set-provider-credentials (vec results)]}))

(reg-event-http
 :ai-chat/fetch-provider-credentials
 fetch-provider-credentials-request
 fetch-provider-credentials-callback)


(defn save-provider-credential-request [_ [params]]
  {:http-xhrio
   {:id        :save-provider-credential
    :method    :post
    :params    (byol/credential-body params)
    :cb-params {:provider (:provider params)}
    :url       "/api/admin/ai-provider-credentials"}})

(defn save-provider-credential-callback [_ [error results {:keys [provider]}]]
  (if (some? error)
    {:dispatch [:ai-chat/set-credential-save-result {:provider provider
                                                     :status   :failed
                                                     :message  (byol/save-error-message error)}]}
    {:dispatch-n [[:ai-chat/set-credential-save-result {:provider provider
                                                        :status   :ok
                                                        :last4    (:last4 results)
                                                        :models   (:models results)}]
                  [:ai-chat/fetch-provider-credentials]
                  [:ai-chat/get-settings]]}))

(reg-event-http
 :ai-chat/save-provider-credential
 save-provider-credential-request
 save-provider-credential-callback)


(defn delete-provider-credential-request [_ [provider capture]]
  {:http-xhrio
   {:id        :delete-provider-credential
    :method    :delete
    :cb-params {:capture capture}
    :url       (str "/api/admin/ai-provider-credentials/" provider)}})

(defn delete-provider-credential-callback [_ [error _ {:keys [capture]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to remove the provider key"]}
    (cond-> {:dispatch-n [[:ai-chat/fetch-provider-credentials]
                          [:ai-chat/get-settings]]}
      capture (assoc :posthog/capture capture))))

(reg-event-http
 :ai-chat/delete-provider-credential
 delete-provider-credential-request
 delete-provider-credential-callback)


(reg-event-db
  :ai-chat/set-provider-credentials
  (fn [db [credentials]]
    (assoc db :ai-chat-provider-credentials credentials)))


(reg-event-db
  :ai-chat/set-credential-save-result
  (fn [db [result]]
    (assoc db :ai-chat-credential-save-result result)))


(reg-event-db
  :ai-chat/clear-credential-save-result
  (fn [db _]
    (dissoc db :ai-chat-credential-save-result)))
